import React, { useEffect, useState } from 'react';

import MinerGame from './index';

/**
 * MinerGameWrapper - компонент-обертка для игры Miner
 * 
 * Для запуска игры из локальной папки ReadyBuilds/Miner:
 * 1. Добавьте параметр ?useLocalBuild=true в URL
 * 2. Или измените значение USE_LOCAL_MINER_BUILD на true
 * 
 * Примеры URL:
 * - Локальная сборка: /miner?useLocalBuild=true
 * - API сборка: /miner?buildId=123
 */

interface MinerGameWrapperProps {
  gameKey: any;
  gameSettings?: any;
}

const MinerGameWrapper: React.FC<MinerGameWrapperProps> = ({ gameKey, gameSettings }) => {
  const [gameBuildData, setGameBuildData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Читаем параметры из URL напрямую внутри useEffect (для standalone билда)
    // Пробуем прочитать из sessionStorage (сохранены до инициализации React Router)
    let queryString = sessionStorage.getItem('minerInitialParams') || window.location.search;
    
    // Если все еще пусто, пробуем извлечь из href
    if (!queryString) {
      const fullUrl = window.location.href;
      if (fullUrl.includes('?')) {
        const urlParts = fullUrl.split('?');
        if (urlParts.length > 1) {
          queryString = '?' + urlParts[1].split('#')[0];
        }
      }
    }
    
    console.log('🔍 Full URL info:', { 
      href: window.location.href, 
      search: window.location.search, 
      savedParams: sessionStorage.getItem('minerInitialParams'),
      queryString: queryString
    });
    
    const urlParams = new URLSearchParams(queryString);
    const buildId = urlParams.get('buildId');
    const useLocalBuild = urlParams.get('useLocalBuild') === 'true';
    
    console.log('🔍 useEffect вызван:', { useLocalBuild, buildId, queryString, allParams: Object.fromEntries(urlParams.entries()) });
    
    // Если используем локальную сборку, загружаем данные из локального файла
    if (useLocalBuild) {
      console.log('🔧 Используем локальную сборку');
      // Пытаемся загрузить данные из локального файла
      const loadLocalBuildData = async () => {
        try {
          // Пытаемся загрузить JSON файл с настройками (пробуем разные пути)
          const possiblePaths = [
            './game-settings.json',  // Относительный путь (если сервер в ReadyBuilds/Miner)
            '/game-settings.json',   // Абсолютный путь от корня
            '/ReadyBuilds/Miner/game-settings.json',  // Полный путь от корня проекта
            'game-settings.json'     // Просто имя файла
          ];
          
          let settingsLoaded = false;
          for (const settingsUrl of possiblePaths) {
            try {
              const settingsResponse = await fetch(settingsUrl);
              if (settingsResponse.ok) {
                // Если JSON файл найден, используем его настройки
                const settingsData = await settingsResponse.json();
                setGameBuildData(settingsData);
                setLoading(false);
                settingsLoaded = true;
                return;
              }
            } catch (e) {
              // Пробуем следующий путь
              continue;
            }
          }
          
          // Если JSON файл не найден, используем fallback данные
          const fallbackData = {
            nameGameHeader: 'Miner Game',
            startGameHeader: 'Нажмите на клетку, чтобы начать игру!',
            endGameHeader: 'Отличная игра!',
            moneyText: 'Выигрыш: 500 монет',
            urlButton: '',
            fileDownloadUrl: '',
            startGameImageUrl: '',
            endGameImageUrl: '',
            musicUrl: ''
          };
          
          setGameBuildData(fallbackData);
        } catch (error) {
          // В случае ошибки используем fallback данные
          const fallbackData = {
            nameGameHeader: 'Miner Game',
            startGameHeader: 'Нажмите на клетку, чтобы начать игру!',
            endGameHeader: 'Отличная игра!',
            moneyText: 'Выигрыш: 500 монет',
            urlButton: '',
            fileDownloadUrl: '',
            startGameImageUrl: '',
            endGameImageUrl: '',
            musicUrl: ''
          };
          
          setGameBuildData(fallbackData);
        }
        
        setLoading(false);
      };
      
      loadLocalBuildData();
      return;
    }

    // Оригинальная логика загрузки через API
    console.log('🔍 Проверяем buildId:', { buildId, useLocalBuild });
    if (!buildId) {
      console.log('❌ Build ID не найден, но useLocalBuild =', useLocalBuild);
      setError('Build ID не найден');
      setLoading(false);
      return;
    }

    // Источник API: ?apiBase=..., затем VITE_API_BASE_URL, затем '/api' (локальный прокси)
    
    const apiBase = urlParams.get('apiBase') || (import.meta as any).env?.VITE_API_BASE_URL || '/api';
    const url = `${apiBase.replace(/\/$/, '')}/public/games/build-data/${buildId}`;
    fetch(url)
      .then(res => {
        if (!res.ok) {
          throw new Error('Ошибка загрузки данных игры');
        }
        return res.json();
      })
      .then(data => {
        setGameBuildData(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []); // Пустой массив - выполняется только при монтировании

  if (loading) {
    return (
      <>
        <style>{`
          @keyframes miner-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .miner-loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-top: 4px solid #fff;
            border-radius: 50%;
            animation: miner-spin 1s linear infinite;
            margin-bottom: 20px;
          }
        `}</style>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: '#008080',
          color: '#fff'
        }}>
          <div className="miner-loading-spinner" />
          <div style={{ fontSize: '18px' }}>Загрузка...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: 'red'
      }}>
        Ошибка: {error}
      </div>
    );
  }

  // Показываем игру только после полной загрузки данных из JSON
  if (!gameBuildData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Данные игры не найдены
      </div>
    );
  }

  const minerSettings = {
    customTitle: gameBuildData?.nameGameHeader || ' ',
    // Текст для старта вынесем отдельно, чтобы не мешать сообщению о поражении
    introText: gameBuildData?.startGameHeader || '',
    // Сообщение о поражении используем из startGameHeader
    loseText: gameBuildData?.startGameHeader || '',
    winText: gameBuildData?.endGameHeader || ' ',
    moneyText: gameBuildData?.moneyText || '',
    winButtonUrl: gameBuildData?.urlButton || '',
    startGameImageUrl: gameBuildData?.startGameImageUrl || '',
    endGameImageUrl: gameBuildData?.endGameImageUrl || '',
    musicUrl: gameBuildData?.musicUrl || '',
    fileDownloadUrl: gameBuildData?.fileDownloadUrl || ''
  };

  return (
    <div className="miner-game-fixed-wrapper">
      <MinerGame gameKey={gameKey} gameSettings={minerSettings} />
    </div>
  );
};

export default MinerGameWrapper; 